/**
 * Main Text & Image Highlighter Hook
 * Simplified and modular architecture
 */

import { selectHighlighterFromSegmentInteraction } from '@/hooks/realtimeDataHelpers';
import { useRealtimeUserSegmentInteraction } from '@/pages/experiences/hooks/realtime/useRealtimeUserSegmentInteraction';
import '@/pages/experiences/show/take/tools/textHighlighter.css';
import { selectExperienceViewMode, useExperienceStore } from '@/store/experienceStore';
import { useTranslate } from '@refinedev/core';
import { useCallback, useEffect, useRef } from 'react';

// Import helper modules
import {
  selectCommentsVisible,
  selectHighlightTextEnabled,
  useRealtimeExperienceStore,
} from '@/pages/experiences/store/realtimeExperienceStore';
import {
  applyImageHighlights,
  handleImageClick,
  updateImageNoteUI,
} from './utils/highlighter/imageHighlightHelpers';
import {
  applyTextHighlights,
  handleTextSelection,
  updateTextNoteUI,
} from './utils/highlighter/textHighlightHelpers';
import { updateNotesVisibility } from './utils/highlighter/uiHelpers';

/**
 * Main hook for text and image highlighting functionality
 */
export const useTextHighlighter = (htmlRef: any, options: any) => {
  const { highlightsIdentifier, handleHighlightOnChange } = options ?? {};
  console.log('🔥 useTextHighlighter initialized', { highlightsIdentifier });

  // NOTE: Access userSegmentInteraction from the realtime store directly instead of props,
  // because props may contain stale data and not reflect the latest real-time updates. - mediaShowSetting is state
  const { segmentInteraction } = useRealtimeUserSegmentInteraction({
    segmentInteractionId: highlightsIdentifier,
  });

  // const { highlightTextEnabled } = useExperienceStore();
  const highlightTextEnabled = useRealtimeExperienceStore(selectHighlightTextEnabled);
  const experienceViewMode = useExperienceStore(selectExperienceViewMode);
  const commentsVisible = useRealtimeExperienceStore(selectCommentsVisible);
  const translate = useTranslate();

  const { text_highlights: textHighlights, image_highlights: imageHighlights } =
    selectHighlighterFromSegmentInteraction(segmentInteraction);

  // Refs to access current data in callbacks
  const textHighlightsRef = useRef<any[]>([]);
  const imageHighlightsRef = useRef<any[]>([]);
  const initialAppliedRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    textHighlightsRef.current = textHighlights;
  }, [textHighlights]);

  useEffect(() => {
    imageHighlightsRef.current = imageHighlights;
  }, [imageHighlights]);

  // ========== UPDATE HANDLERS ==========
  /**
   * Update text highlights in store
   */
  const updateTextHighlights = useCallback(
    (highlight: any, action = 'add') => {
      console.log('updateTextHighlights', textHighlightsRef.current);
      const updated = [...textHighlightsRef.current];

      if (action === 'remove') {
        const index = updated.findIndex((h) => h.id === highlight.id);
        if (index > -1) updated.splice(index, 1);
      } else {
        const index = updated.findIndex((h) => h.id === highlight.id);
        if (index > -1) {
          updated[index] = highlight;
        } else {
          updated.push(highlight);
        }
      }

      handleHighlightOnChange({
        highlighter: {
          text_highlights: updated,
          image_highlights: imageHighlightsRef.current,
        },
      });
    },
    [handleHighlightOnChange]
  );

  /**
   * Update image highlights in store
   */
  const updateImageHighlights = useCallback(
    (highlight: any, action = 'add') => {
      const updated = [...imageHighlightsRef.current];

      if (action === 'remove') {
        const index = updated.findIndex((h) => h.id === highlight.id);
        if (index > -1) updated.splice(index, 1);
      } else {
        const index = updated.findIndex((h) => h.id === highlight.id);
        if (index > -1) {
          updated[index] = highlight;
        } else {
          updated.push(highlight);
        }
      }

      handleHighlightOnChange({
        highlighter: {
          text_highlights: textHighlightsRef.current,
          image_highlights: updated,
        },
      });
    },
    [handleHighlightOnChange]
  );

  // ========== EFFECT 1: Apply saved highlights (always visible) ==========

  useEffect(() => {
    const isValidMode = ['apPreviewTest', 'apTakeTest'].includes(experienceViewMode);
    if (!isValidMode || !htmlRef.current) return;

    // Check if highlight spans already exist in DOM
    const existingHighlights = htmlRef.current.querySelectorAll('.highlighted-text');
    const hasExistingSpans = existingHighlights.length > 0;

    // Clear existing buttons and notes before re-applying (prevents duplicates)
    existingHighlights.forEach((span: Element) => {
      const buttonWrapper = span.querySelector('.highlight-buttons-wrapper');
      const noteDisplay = span.querySelector('.minimized-note-display');

      // For group members, remove any buttons/notes that shouldn't be there
      if ((span as HTMLElement).dataset.spanGroupMember) {
        if (buttonWrapper) {
          console.warn('Removing buttons from group member span');
          buttonWrapper.remove();
        }
        if (noteDisplay) {
          console.warn('Removing note from group member span');
          noteDisplay.remove();
        }
        return;
      }

      // For main spans, clear to refresh
      if (buttonWrapper) buttonWrapper.remove();
      if (noteDisplay) noteDisplay.remove();
    });

    // ========== Apply saved text highlights ==========
    // This section handles both initial load and page refreshes
    if (textHighlights.length > 0) {
      if (!initialAppliedRef.current && !hasExistingSpans) {
        // ========== SCENARIO 1: First Load (No Existing Spans) ==========
        // The highlight data exists in state, but spans don't exist in DOM yet
        // ACTION: Create all highlight spans from scratch using saved offsets
        applyTextHighlights(
          textHighlights,
          highlightsIdentifier,
          htmlRef.current,
          updateTextHighlights,
          translate,
          commentsVisible
        );
        initialAppliedRef.current = true;
      } else {
        // ========== SCENARIO 2: Page Refresh or Re-render (Spans Already Exist) ==========
        // The highlight spans already exist in the HTML, but:
        // - UI components (buttons, notes) may need to be refreshed
        // - Event listeners need to be re-attached (they don't survive page refresh)
        // ACTION: Find existing spans and update their UI + re-attach listeners

        textHighlights.forEach((highlight) => {
          // Find the main span for this highlight
          // For cross-element highlights (span groups), we need to find the "main" span
          // which has data-span-group attribute (not data-span-group-member)

          // STEP 1: Try to find span with data-span-group (main span in a group)
          let span = htmlRef.current.querySelector(
            `.highlighted-text[data-span-group="${highlight.id}"]`
          );

          // STEP 2: If not found, try to find any span with this highlight-id that's not a member
          // This handles simple (non-group) highlights
          if (!span) {
            span = htmlRef.current.querySelector(
              `.highlighted-text[data-highlight-id="${highlight.id}"]:not([data-span-group-member])`
            );
          }

          if (span) {
            // ========== Handle Span Groups (Cross-Element Highlights) ==========
            // Span groups need special handling because:
            // 1. They have multiple spans that need coordinated hover behavior
            // 2. Event listeners don't survive page refreshes
            // 3. We need to re-create UI elements on the new cloned spans

            if ((span as HTMLElement).dataset.spanGroup) {
              const groupId = (span as HTMLElement).dataset.spanGroup;

              // Find all member spans (spans with data-span-group-member matching this group ID)
              const memberSpans = htmlRef.current.querySelectorAll(
                `[data-span-group-member="${groupId}"]`
              );

              // IMPORTANT: Store hide timeout in a closure that persists across all event listeners
              // This shared timeout prevents flickering when moving mouse between spans
              let hideTimeout: NodeJS.Timeout | null = null;

              // ========== Clone and Replace Spans ==========
              // WHY: Cloning is the cleanest way to remove old event listeners
              // We can't just add new listeners because old ones would still exist

              // Clone the main span
              const newMainSpan = (span as HTMLElement).cloneNode(true) as HTMLElement;
              (span as HTMLElement).parentNode?.replaceChild(newMainSpan, span as HTMLElement);

              // Clone all member spans
              const newMemberSpans: HTMLElement[] = [];
              memberSpans.forEach((memberSpan: Element) => {
                const newMemberSpan = memberSpan.cloneNode(true) as HTMLElement;
                memberSpan.parentNode?.replaceChild(newMemberSpan, memberSpan);
                newMemberSpans.push(newMemberSpan);
              });

              // ========== Update UI on the NEW main span ==========
              // CRITICAL: Must call this AFTER cloning, on the NEW span that's in the DOM
              // If we update the old span before cloning, the UI elements get lost
              updateTextNoteUI(
                newMainSpan,
                highlight,
                updateTextHighlights,
                translate,
                commentsVisible
              );

              // ========== Re-attach Event Listeners ==========
              // Now add fresh listeners to the NEW cloned elements
              const allNewSpans = [newMainSpan, ...newMemberSpans];

              allNewSpans.forEach((newSpan) => {
                // ========== MOUSEENTER Handler ==========
                // Triggered when mouse enters ANY span in the group
                newSpan.addEventListener('mouseenter', () => {
                  // Cancel any pending hide timeout to prevent premature hiding
                  // IMPORTANT: This prevents flickering when moving between spans
                  if (hideTimeout) {
                    clearTimeout(hideTimeout);
                    hideTimeout = null;
                  }

                  // Get UI elements from the NEW main span (they're only there)
                  const buttonWrapper = newMainSpan.querySelector('.highlight-buttons-wrapper');
                  const minimizedNote = newMainSpan.querySelector('.minimized-note-display');

                  // Show action buttons when hovering ANY span in the group
                  if (buttonWrapper) {
                    (buttonWrapper as HTMLElement).style.display = 'flex';
                  }

                  // Increase note opacity for better visibility during hover
                  if (minimizedNote) {
                    (minimizedNote as HTMLElement).style.opacity = '1';
                  }
                });

                // ========== MOUSELEAVE Handler ==========
                // Triggered when mouse leaves ANY span in the group
                newSpan.addEventListener('mouseleave', () => {
                  // Clear any existing timeout to prevent multiple timeouts running
                  if (hideTimeout) {
                    clearTimeout(hideTimeout);
                  }

                  // Set new timeout to hide buttons/reset note opacity after a delay
                  // The delay provides smooth transitions when moving between spans
                  hideTimeout = setTimeout(() => {
                    // IMPORTANT: Check if mouse is still hovering ANY span in the group
                    const isHoveringGroup = allNewSpans.some((s) => s.matches(':hover'));

                    // Also check if hovering the buttons themselves
                    const buttonWrapper = newMainSpan.querySelector('.highlight-buttons-wrapper');
                    const minimizedNote = newMainSpan.querySelector('.minimized-note-display');
                    const isHoveringButtons = buttonWrapper?.matches(':hover');

                    // Only hide if mouse has truly left the entire highlight group
                    if (!isHoveringGroup && !isHoveringButtons) {
                      if (buttonWrapper) {
                        (buttonWrapper as HTMLElement).style.display = 'none';
                      }
                      if (minimizedNote) {
                        (minimizedNote as HTMLElement).style.opacity = '0.5';
                      }
                    }
                    hideTimeout = null;
                  }, 100); // 100ms delay for smooth UX
                });
              });
            } else {
              // ========== Handle Simple Highlights (Non-Group) ==========
              // Simple highlights are easier - just one span, no special hover coordination needed
              updateTextNoteUI(
                span as HTMLElement,
                highlight,
                updateTextHighlights,
                translate,
                commentsVisible
              );
            }
          }
        });
        // Mark as applied if we found and updated existing spans
        if (hasExistingSpans) {
          initialAppliedRef.current = true;
        }
      }
    }

    // Apply saved image highlights
    applyImageHighlights(
      imageHighlights,
      highlightsIdentifier,
      htmlRef.current,
      updateImageHighlights,
      translate,
      commentsVisible
    );

    // Update notes visibility
    updateNotesVisibility(htmlRef.current, commentsVisible);
  }, [
    experienceViewMode,
    highlightsIdentifier,
    commentsVisible,
    imageHighlights,
    textHighlights,
    updateTextHighlights,
    updateImageHighlights,
    translate,
  ]);

  // ========== EFFECT 2: Setup event listeners for creating new highlights ==========

  useEffect(() => {
    const isValidMode = ['apPreviewTest', 'apTakeTest'].includes(experienceViewMode);
    if (!isValidMode || !htmlRef.current) return;

    // Only add event listeners when highlighting is enabled
    if (!highlightTextEnabled) {
      console.log('Text highlighting disabled, skipping event listeners');

      // Clean up any open tooltips and buttons when disabling
      if (htmlRef.current) {
        try {
          // Remove note editing tooltips safely
          document.querySelectorAll('.note-editing-tooltip').forEach((t) => {
            try {
              if ((t as any)._cleanup) {
                (t as any)._cleanup();
              }
              if (document.body.contains(t)) {
                t.remove();
              }
            } catch (e) {
              console.warn('Error removing note tooltip:', e);
            }
          });

          // Remove image highlight options tooltips
          document.querySelectorAll('.image-highlight-options-tooltip').forEach((t) => {
            try {
              if (document.body.contains(t)) {
                t.remove();
              }
            } catch (e) {
              console.warn('Error removing image options tooltip:', e);
            }
          });

          // Hide all button wrappers (but don't remove them, let React handle it)
          const buttonWrappers = htmlRef.current.querySelectorAll(
            '.highlight-buttons-wrapper, .image-highlight-buttons-wrapper'
          );
          buttonWrappers.forEach((wrapper: any) => {
            try {
              wrapper.style.display = 'none';
            } catch (e) {
              console.warn('Error hiding button wrapper:', e);
            }
          });
        } catch (error) {
          console.warn('Error during highlighter cleanup:', error);
        }
      }

      return;
    }

    console.log('Text highlighting enabled, adding event listeners');

    // ========== EVENT HANDLERS ==========

    const handleMouseUp = () => {
      handleTextSelection(highlightsIdentifier, updateTextHighlights, translate, commentsVisible);
    };

    const handleImageClickEvent = (e: MouseEvent) => {
      handleImageClick(
        e,
        highlightsIdentifier,
        imageHighlightsRef.current,
        updateImageHighlights,
        translate,
        commentsVisible
      );
    };

    // Apply crosshair cursor to images
    const applyCrosshairCursor = () => {
      const styleId = 'image-highlighter-cursor-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `.image-highlighter-active img { cursor: crosshair !important; }`;
        document.head.appendChild(style);
      }

      const images = htmlRef.current.querySelectorAll('img');
      images.forEach((img: HTMLImageElement) => {
        img.style.setProperty('cursor', 'crosshair', 'important');
        img.classList.add('image-highlighter-active');
      });
      htmlRef.current.classList.add('image-highlighter-active');
    };

    const removeCrosshairCursor = () => {
      const images = htmlRef.current?.querySelectorAll('img');
      images?.forEach((img: HTMLImageElement) => {
        img.style.removeProperty('cursor');
        img.classList.remove('image-highlighter-active');
      });
      htmlRef.current?.classList.remove('image-highlighter-active');

      // Shared <style> tag: keep it while ANY other scope is still armed
      // (R2-G56/GR10 — one instance disarming must not strip the others).
      if (!document.querySelector('.image-highlighter-active')) {
        const style = document.getElementById('image-highlighter-cursor-style');
        if (style) style.remove();
      }
    };

    applyCrosshairCursor();

    // Observe new images added dynamically
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as Element;
            if (el.tagName === 'IMG') {
              (el as HTMLImageElement).style.setProperty('cursor', 'crosshair', 'important');
              el.classList.add('image-highlighter-active');
            }
            el.querySelectorAll?.('img')?.forEach((img: Element) => {
              (img as HTMLImageElement).style.setProperty('cursor', 'crosshair', 'important');
              img.classList.add('image-highlighter-active');
            });
          }
        });
      });
    });

    observer.observe(htmlRef.current, { childList: true, subtree: true });

    // Attach event listeners
    htmlRef.current.addEventListener('mouseup', handleMouseUp);
    // **ADDED:** Add touchend listener for touch devices
    htmlRef.current.addEventListener('touchend', handleMouseUp);
    htmlRef.current.addEventListener('click', handleImageClickEvent);

    // Cleanup
    return () => {
      try {
        observer.disconnect();
        removeCrosshairCursor();
        htmlRef.current?.removeEventListener('mouseup', handleMouseUp);
        htmlRef.current?.removeEventListener('touchend', handleMouseUp);
        htmlRef.current?.removeEventListener('click', handleImageClickEvent);

        // Clean up highlighter-specific tooltips only
        document.querySelectorAll('.note-editing-tooltip').forEach((t) => {
          try {
            if ((t as any)._cleanup) {
              (t as any)._cleanup();
            }
            if (document.body.contains(t)) {
              t.remove();
            }
          } catch (e) {
            console.warn('Error removing note tooltip in cleanup:', e);
          }
        });

        // Remove image highlight options tooltips
        document.querySelectorAll('.image-highlight-options-tooltip').forEach((t) => {
          try {
            if (document.body.contains(t)) {
              t.remove();
            }
          } catch (e) {
            console.warn('Error removing image options tooltip in cleanup:', e);
          }
        });
      } catch (error) {
        console.warn('Error in effect cleanup:', error);
      }
    };
  }, [highlightTextEnabled, experienceViewMode, highlightsIdentifier, commentsVisible]);

  // Reset initial application flag when changing questions
  useEffect(() => {
    initialAppliedRef.current = false;
  }, [highlightsIdentifier]);

  // ========== EFFECT: Update notes visibility when commentsVisible changes ==========

  useEffect(() => {
    if (!htmlRef.current) return;

    // Update all text highlight notes - but only on main spans, not group members
    // Group all highlights by ID to avoid processing the same highlight multiple times
    const processedHighlightIds = new Set<string>();

    const textSpans = htmlRef.current.querySelectorAll('.highlighted-text[data-has-note="true"]');
    textSpans.forEach((span: any) => {
      // Skip span group members - only update the main span
      if (span.dataset.spanGroupMember) {
        return;
      }

      const id = span.dataset.highlightId;

      // Skip if we've already processed this highlight ID
      if (processedHighlightIds.has(id)) {
        return;
      }
      processedHighlightIds.add(id);

      const highlight = textHighlightsRef.current.find((h) => h.id === id);
      if (highlight) {
        updateTextNoteUI(span, highlight, updateTextHighlights, translate, commentsVisible);
      }
    });

    // Update all image highlight notes
    const imageOverlays = htmlRef.current.querySelectorAll('.image-highlight-overlay');
    imageOverlays.forEach((overlay: any) => {
      const id = overlay.dataset.highlightId;
      const highlight = imageHighlightsRef.current.find((h) => h.id === id);
      if (highlight) {
        // Call updateImageNoteUI to properly update notes and buttons
        updateImageNoteUI(overlay, highlight, updateImageHighlights, translate, commentsVisible);
      }
    });

    // Update visibility of all notes and buttons
    updateNotesVisibility(htmlRef.current, commentsVisible);
  }, [commentsVisible, updateTextHighlights, updateImageHighlights, translate]);

  // ========== EFFECT: Cleanup on unmount ==========

  // R2-G56 / R2-GR10: cleanup is SCOPED to this instance's container. The
  // hook mounts once per highlighter-scoped APRenderHtml (stem, passage,
  // resource image…), so a per-instance unmount (paging, pinning a segment,
  // a pane collapsing on resize) must never strip other live instances'
  // scope classes, Edit/Delete button wrappers, or the shared armed flag —
  // the old document-wide cleanup did exactly that.
  useEffect(() => {
    // Effects run post-commit, so the ref is populated here.
    const scopeEl: HTMLElement | null = htmlRef.current;
    return () => {
      console.log('🧹 Cleaning up highlighter on unmount');

      try {
        if (scopeEl) {
          // Remove cursor styling + scope class from OWN subtree only
          [scopeEl, ...Array.from(scopeEl.querySelectorAll('.image-highlighter-active'))].forEach(
            (el) => {
              try {
                if (el.tagName === 'IMG') {
                  (el as HTMLImageElement).style.removeProperty('cursor');
                }
                el.classList.remove('image-highlighter-active');
              } catch (e) {
                console.warn('Error removing cursor from element:', e);
              }
            }
          );
        }

        // The shared cursor <style> tag: remove only when no other scope is
        // still armed (another live instance may be mid-highlighting).
        if (!document.querySelector('.image-highlighter-active')) {
          const style = document.getElementById('image-highlighter-cursor-style');
          if (style && document.head.contains(style)) {
            style.remove();
          }
        }

        // Close body-appended note editors belonging to THIS scope's marks
        document.querySelectorAll('.note-editing-tooltip').forEach((t) => {
          try {
            const id = t.getAttribute('data-highlight-id');
            const ownedByScope =
              !!scopeEl && !!id && !!scopeEl.querySelector(`[data-highlight-id="${CSS.escape(id)}"]`);
            if (!ownedByScope) return;
            if ((t as any)._cleanup) {
              (t as any)._cleanup();
            }
            if (document.body.contains(t)) {
              t.remove();
            }
          } catch (e) {
            console.warn('Error removing note tooltip on unmount:', e);
          }
        });

        // Image options menu (transient, un-attributable) — safe to close
        document.querySelectorAll('.image-highlight-options-tooltip').forEach((t) => {
          try {
            if (document.body.contains(t)) {
              t.remove();
            }
          } catch (e) {
            console.warn('Error removing image options tooltip on unmount:', e);
          }
        });

        // Button wrappers: own subtree only (other cards keep Edit/Delete)
        scopeEl
          ?.querySelectorAll('.highlight-buttons-wrapper, .image-highlight-buttons-wrapper')
          .forEach((wrapper) => {
            try {
              if (document.body.contains(wrapper)) {
                wrapper.remove();
              }
            } catch (e) {
              console.warn('Error removing button wrapper on unmount:', e);
            }
          });
      } catch (error) {
        console.warn('Error during unmount cleanup:', error);
      }
    };
    // htmlRef is a stable ref object — mount-once capture is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {};
};
